import { Flex, Box, DataList, Separator } from '@volue/wave-react';
import React from 'react';

import { isCalculationTacticResultUnavailable } from '@/features/execute-calculation-set/overmind/calculation/actions';
import { useAppState } from '@/overmind';

import type { SelectedGridElement } from '../../../overmind/calculation';
import { EvaluatedResultComponent } from '../../EvaluatedResultComponent';
import { LoadFlowDetailsHeader } from './LoadFlowDetailsHeader';

type SelectedEnergyConsumer = {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<'EnergyConsumer'>;
};

export const EnergyConsumerDetails: React.FunctionComponent<{
  energyConsumer: SelectedEnergyConsumer;
}> = ({ energyConsumer }) => {
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;
  const getLoadSourceValue = (loadSource?: string) =>
    loadSource == null ? '' : `(${loadSource})`;

  const calculationTactic = useAppState().calculation.calculationTactic.value;
  if (isCalculationTacticResultUnavailable(calculationTactic)) return null;
  const voltagelevel = calculationTactic.calculationSetResult.voltageLevel;

  return (
    <>
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'ALL_LOAD_FLOW' ||
        (tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW' &&
          tacticResultBrowsingFilter.scenarioIds.length > 0)) &&
        energyConsumer.gridElement.loadflow.type === 'LOAD_FLOW_DETAILS' && (
          <>
            {energyConsumer.gridElement.loadflow.type ===
              'LOAD_FLOW_DETAILS' && (
              <Flex flow="column" gap={'spacingXs'}>
                <LoadFlowDetailsHeader
                  scenarioName={
                    energyConsumer.gridElement.loadflow.details.scenarioName
                  }
                />
                {
                  <DataList className="mHxl" ratio={[3, 1]}>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>{'Voltage'}</DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        <EvaluatedResultComponent
                          result={
                            energyConsumer.gridElement.loadflow.details.results
                              .voltage
                          }
                        />
                      </Flex>
                    </Flex>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>{'Voltage Drop'}</DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        <EvaluatedResultComponent
                          result={
                            energyConsumer.gridElement.loadflow.details.results
                              .voltageDiffPercent
                          }
                          unitLabel
                        />
                      </Flex>
                    </Flex>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>{'Power Factor'}</DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        <EvaluatedResultComponent
                          result={
                            energyConsumer.gridElement.loadflow.details.results
                              .voltageCosinePhi
                          }
                        />
                      </Flex>
                    </Flex>
                    <Separator />
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>
                        {'Active Power '}
                        {getLoadSourceValue(
                          energyConsumer.gridElement.loadflow.details.inputs
                            .loadSource
                        )}
                      </DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        {energyConsumer.gridElement.loadflow.details.inputs.p.toString()}
                      </Flex>
                    </Flex>
                    <Flex as={DataList.Row} main="space-between">
                      <DataList.Label>
                        {'Reactive Power '}
                        {getLoadSourceValue(
                          energyConsumer.gridElement.loadflow.details.inputs
                            .loadSource
                        )}
                      </DataList.Label>
                      <Flex main={'end'} as={DataList.Value}>
                        {energyConsumer.gridElement.loadflow.details.inputs.q.toString()}
                      </Flex>
                    </Flex>
                  </DataList>
                }
              </Flex>
            )}
          </>
        )}
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'SHORT_CIRCUIT') &&
        energyConsumer.gridElement.shortCircuit.type ===
          'SHORT_CIRCUIT_DETAILS' && (
          <>
            {energyConsumer.gridElement.shortCircuit.type ===
              'SHORT_CIRCUIT_DETAILS' && (
              <Flex flow="column" gap={'spacingXs'}>
                <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
                  {'Short Circuit'}
                </Box>
                {energyConsumer.gridElement.objectType === 'EnergyConsumer' &&
                  energyConsumer.gridElement.shortCircuit.evaluatedResults
                    .objectType === 'EnergyConsumer' && (
                    <DataList className="mHxl" ratio={[3, 1]}>
                      {voltagelevel === 'LowVoltage' && (
                        <>
                          {energyConsumer.gridElement.shortCircuit
                            .evaluatedResults.results.protectingReleaseTime
                            .type === 'CALCULATION_RESULT_AVAILABLE' && (
                            <Flex as={DataList.Row} main="space-between">
                              <DataList.Label>
                                {'Protecting release time'}
                              </DataList.Label>
                              <Flex main={'end'} as={DataList.Value}>
                                <EvaluatedResultComponent
                                  result={
                                    energyConsumer.gridElement.shortCircuit
                                      .evaluatedResults.results
                                      .protectingReleaseTime.result
                                  }
                                />
                              </Flex>
                            </Flex>
                          )}
                          {energyConsumer.gridElement.shortCircuit
                            .evaluatedResults.results.protectingReleaseTime
                            .type === 'CALCULATION_RESULT_NOT_AVAILABLE' && (
                            <Flex as={DataList.Row} main="space-between">
                              <DataList.Label>
                                {'Protecting release time'}
                              </DataList.Label>
                              <Flex main={'end'} as={DataList.Value}>
                                {'N/A'}
                              </Flex>
                            </Flex>
                          )}
                        </>
                      )}

                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase1Min.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase1Min'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase1Min.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase1Max.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase1Max'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase1Max.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase2Min.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase2Min'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase2Min.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase3Max.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase3Max'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase3Max.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                    </DataList>
                  )}
              </Flex>
            )}
          </>
        )}
    </>
  );
};
